import { IStoredCheckoutBasket } from "@checkout/types";
import { BaseAPI } from "@utils/base-api";

import { CheckoutStatus, type CheckoutBasket } from "@/primary-models";

import {
  CreateCheckoutBasketResponse,
  IBasketConfirmedRequest,
  IBasketConfirmedResponse,
} from "./basket.types";
import {
  IGetPriceEstimateRequest,
  IGetPriceEstimateResponse,
} from "./pricing.types";
import {
  CHECKOUT_PRICE_ESTIMATE_PATH,
  CHECKOUT_PAYMENTS_BASKET_CONFIRMED_PATH,
  CHECKOUT_V2_START_CHECKOUT_PATH,
  CHECKOUT_V2_START_CHECKOUT_USER_PARAMETER,
  CHECKOUT_CARD_READER_FETCH_ALL_PATH,
} from "./endpoints";
import { PaymentCollectionTarget } from "./payment-collection.types";
import { ICardReader } from "./card-reader.types";

export class CheckoutAPI extends BaseAPI {

  private static readonly POLL_ATTEMPTS = 45;

  private static readonly POLL_INTERVAL_MS = 2000;

  /**
   * This endpoint is called when a Setup/PaymentIntent associated with a basket has been confirmed
   * (either successfully or unsuccessfully), so that we can finish processing the basket and related charge.
   */
  public async postPaymentsBasketConfirmed(request: IBasketConfirmedRequest): Promise<IBasketConfirmedResponse> {
    const response = await this.axios.post(CHECKOUT_PAYMENTS_BASKET_CONFIRMED_PATH, request);
    return response.data;
  }

  async getBasket(basketId: string) {

    const response = await this.axios.get<CheckoutBasket>(`/basket/${basketId}`);

    return response.data;
  }

  /** @return null if the basket was still in progress after poling for 90 seconds
   * or the not-in-progress basket, if one was found */
  async pollBasket(basketId: string): Promise<CheckoutBasket | null> {

    // Try every 2 secs for 90 seconds

    for (let i = 0; i < CheckoutAPI.POLL_ATTEMPTS; i++) {

      const basket = await this.getBasket(basketId);

      if (basket.status !== CheckoutStatus.IN_PROGRESS && basket.status !== CheckoutStatus.DRAFT) {
        return basket;
      }

      await sleep(CheckoutAPI.POLL_INTERVAL_MS);
    }

    return null;

  }

  /**
   * Validates the recipe selection and calculates pricing with discounts for the subscriptions in the basket
   */
  public async getPriceEstimate(request: IGetPriceEstimateRequest): Promise<IGetPriceEstimateResponse> {
    const response = await this.axios.post(CHECKOUT_PRICE_ESTIMATE_PATH, request);
    return response.data as IGetPriceEstimateResponse;
  }

  /** Creates a checkout basket for the user and starts the checkout process for them.
   * Returns the basketId and stripeIntentSecret if successful, that will then
   * be used to process the payment.
   * May also return a result indicating that the customer has already checked out. */
  public async startCheckout(
    userId: string,
    basket: IStoredCheckoutBasket,
    paymentCollectionTarget: PaymentCollectionTarget = PaymentCollectionTarget.WEBSITE,
  ): Promise<CreateCheckoutBasketResponse> {
    try {
      const response = await this.axios.post(
        CHECKOUT_V2_START_CHECKOUT_PATH.replace(
          `:${CHECKOUT_V2_START_CHECKOUT_USER_PARAMETER}`,
          userId,
        ),
        {
          basket,
          paymentCollectionTarget,
        },
      );

      return response.data as CreateCheckoutBasketResponse;
    } catch (error) {
      console.error("Error creating checkout basket", error);
      // Network errors or 500s should be normalised
      return {
        basketId: "",
        isExisting: false,
        validationError: {
          field: "network",
          message: "Network request failed",
          errorType: "network_error",
        },
      };
    }
  }

  public async getCardReaders(): Promise<ICardReader[]> {
    const response = await this.axios.get(CHECKOUT_CARD_READER_FETCH_ALL_PATH);
    return response.data;
  }

}

const sleep = (n: number): Promise<void> => new Promise((res) => {
  setTimeout(() => {
    res();
  }, n);
});
